<template>
  <v-text-field
    ref="textField"
    v-model="searchQuery"
    variant="outlined"
    density="compact"
    hide-details
    :placeholder="selected?.length ? '' : placeholder"
    :disabled="disabled"
    class="full-width"
    @keydown.delete="$emit('deleteKeyDown')"
    @focus="$emit('focus')"
  >
    <template #prepend-inner>
      <DpplChip
        v-for="option in selected.slice(0, 3)"
        :key="`chip${option[idKey]}`"
      >
        <template #label>
          <span class="chip-label">
            {{ typeof labelKey === 'function' ? labelKey(option) : option[labelKey] }}
          </span>
        </template>
        <template #default>
          <v-icon
            color="basicRed"
            class="ml-1 close-all-icon"
            icon="$close-circle-outline"
            @click="$emit('removeOption', option)"
          />
        </template>
      </DpplChip>
      <span
        v-if="selected.length > 3"
        class="and-more"
      >and {{ selected.length - 3 }} more</span>
    </template>
    <template
      v-if="selected.length || isSelect"
      #append-inner
    >
      <v-icon
        v-if="selected.length"
        size="small"
        color="iconDarkest"
        class="circle-close"
        icon="$close-circle-outline"
        @click="$emit('update:selectedFilters', [])"
      />
      <v-icon
        v-if="isSelect"
        color="iconDarkest"
        class="chevron-icon"
        size="small"
        :icon="opened ? '$chevron-up' : '$chevron-down'"
        @click.self="$emit('toggleDropdown')"
      />
    </template>
  </v-text-field>
</template>

<script setup>
import DpplChip from '@/components/datapeople/DpplChip.vue';
import { computed, ref, onMounted } from 'vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: 'Select',
  },
  selected: {
    type: Array,
    default: () => ([]),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  async: {
    type: Boolean,
    default: false,
  },
  isSelect: {
    type: Boolean,
    default: false,
  },
  opened: {
    type: Boolean,
    default: false,
  },
  labelKey: {
    type: [String, Function],
    default: 'label',
  },
  idKey: {
    type: String,
    default: 'id',
  },
});

const emits = defineEmits(['update:selectedFilters', 'update:modelValue', 'deleteKeyDown', 'removeOption', 'toggleDropdown', 'focus']);

const searchQuery = computed({
  get: () => props.modelValue,
  set: (value) => emits('update:modelValue', value),
});

const textField = ref(null);

onMounted(() => {
  if (!props.isSelect) textField.value.focus();
});
</script>

<style lang="less" scoped>
.full-width {
  width: 100%;
}

:deep(.v-field) {
  display: flex;
  flex-wrap: wrap;
  min-width: 215px;
  padding-right: 24px;

  input, input::placeholder {
    opacity: 1;
    color: @text-darkest;
    font-weight: 300;
  }

  &.v-field--focused {
    .v-field__outline__start, .v-field__outline__end {
      border-color: @text-brand !important;
      border-width: 2px !important
    }

    .v-field__outline__start {
      border-right-width: 0px !important;
    }

    .v-field__outline__end {
      border-left-width: 0px !important;
    }
  }

  .v-field__prepend-inner {
    flex-wrap: wrap;

    .v-chip {
      margin: 4px 4px 0px 0px;
      background: fade(@basic-red, 5%);
      padding: 0px 8px !important;

      .v-chip__content {
        width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-weight: 300;
        font-size: 12px;
        color: @text-darkest;
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .chip-label {
        width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .v-chip__underlay {
        background: transparent;
      }
    }
  }

  .v-field__append-inner {
    position: absolute;
    right: 5px;
    top: calc(50% - 9px);
  }
}

:deep(.v-field__outline) {
  .v-field__outline__start, .v-field__outline__end {
    border: 1px solid fade(@grey-900, 50%) !important;

  }

  .v-field__outline__start {
    border-right-width: 0px !important;
  }

  .v-field__outline__end {
    border-left-width: 0px !important;
  }
}

:deep(.v-chip) {
  height: 28px !important;
  max-width: 215px;
  border-radius: 50px;
}

.and-more {
  font-size: 12px;
  font-weight: 600;
  padding: 1px 0;
  margin: 2px 0;
  cursor: default;
  color: fade(@grey-900, 30%);
}

:deep(.circle-close):hover {
  color: @basic-red !important;
  opacity: 1;
}

:deep(.chevron-icon) {
  transform: translateY(-1px);
}
</style>
